// @effect-diagnostics globalDate:off - This dependency-free contract is shared with the Convex runtime; Date only canonicalizes validated instants.
/** Private Actor-in-World VIEW receipts. No World or Goal state is written. */
export interface ThreadCompletionRead {
  readonly threadKey: string;
  readonly completedAt?: string;
  readonly observedAt?: number;
  readonly unreadCompletedAt?: string;
  readonly unreadAt?: number;
}
export const COMPLETION_READS_MAX = 2048;
export const COMPLETION_READ_KEY_MAX = 1024;
const validDate = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length <= 40 &&
  /^\d{4}-\d{2}-\d{2}T/u.test(value) &&
  Number.isFinite(Date.parse(value)) &&
  Date.parse(value) >= 0;
const sortEntries = (a: ThreadCompletionRead, b: ThreadCompletionRead) =>
  a.threadKey < b.threadKey ? -1 : a.threadKey > b.threadKey ? 1 : 0;
const canonicalEntry = (entry: ThreadCompletionRead): ThreadCompletionRead => ({
  threadKey: entry.threadKey,
  ...(entry.completedAt === undefined
    ? {}
    : { completedAt: new Date(entry.completedAt).toISOString(), observedAt: entry.observedAt! }),
  ...(entry.unreadCompletedAt === undefined
    ? {}
    : {
        unreadCompletedAt: new Date(entry.unreadCompletedAt).toISOString(),
        unreadAt: entry.unreadAt!,
      }),
});
const validClock = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
export function parseThreadCompletionReads(value: unknown): ThreadCompletionRead[] | undefined {
  if (!Array.isArray(value) || value.length > COMPLETION_READS_MAX) return undefined;
  const entries: ThreadCompletionRead[] = [];
  const keys = new Set<string>();
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return undefined;
    const e = entry as Record<string, unknown>;
    if (
      Object.keys(e).some(
        (key) =>
          !["threadKey", "completedAt", "observedAt", "unreadCompletedAt", "unreadAt"].includes(
            key,
          ),
      )
    )
      return undefined;
    if (
      typeof e.threadKey !== "string" ||
      !e.threadKey.trim().length ||
      /[\u0000-\u001f]/u.test(e.threadKey) ||
      e.threadKey.length > COMPLETION_READ_KEY_MAX ||
      keys.has(e.threadKey)
    )
      return undefined;
    const read = e.completedAt !== undefined || e.observedAt !== undefined;
    const unread = e.unreadCompletedAt !== undefined || e.unreadAt !== undefined;
    if (
      (!read && !unread) ||
      (read && (!validDate(e.completedAt) || !validClock(e.observedAt))) ||
      (unread && (!validDate(e.unreadCompletedAt) || !validClock(e.unreadAt)))
    )
      return undefined;
    keys.add(e.threadKey);
    entries.push(
      canonicalEntry({
        threadKey: e.threadKey,
        ...(read
          ? { completedAt: e.completedAt as string, observedAt: e.observedAt as number }
          : {}),
        ...(unread
          ? { unreadCompletedAt: e.unreadCompletedAt as string, unreadAt: e.unreadAt as number }
          : {}),
      }),
    );
  }
  if (new TextEncoder().encode(JSON.stringify(entries)).byteLength > 256 * 1024) return undefined;
  return entries.sort(sortEntries);
}
export function mergeThreadCompletionReads(
  current: readonly ThreadCompletionRead[],
  incoming: readonly ThreadCompletionRead[],
): ThreadCompletionRead[] {
  const entries = new Map(current.map((entry) => [entry.threadKey, canonicalEntry(entry)]));
  for (const input of incoming) {
    const next = canonicalEntry(input);
    const old = entries.get(next.threadKey);
    if (!old) {
      entries.set(next.threadKey, next);
      continue;
    }
    const nextDate = Date.parse(next.completedAt ?? "");
    const oldDate = Date.parse(old.completedAt ?? "");
    const takeRead =
      Number.isFinite(nextDate) &&
      (!Number.isFinite(oldDate) ||
        nextDate > oldDate ||
        (nextDate === oldDate && next.observedAt! > old.observedAt!));
    const takeUnread =
      next.unreadAt !== undefined &&
      (old.unreadAt === undefined ||
        next.unreadAt > old.unreadAt ||
        (next.unreadAt === old.unreadAt &&
          Date.parse(next.unreadCompletedAt!) > Date.parse(old.unreadCompletedAt!)));
    const read = takeRead ? next : old;
    const unread = takeUnread ? next : old;
    entries.set(next.threadKey, {
      threadKey: next.threadKey,
      ...(read.completedAt === undefined
        ? {}
        : { completedAt: read.completedAt, observedAt: read.observedAt! }),
      ...(unread.unreadCompletedAt === undefined
        ? {}
        : { unreadCompletedAt: unread.unreadCompletedAt, unreadAt: unread.unreadAt! }),
    });
  }
  return [...entries.values()].sort(sortEntries);
}
export function completionReadCursor(entry: ThreadCompletionRead | undefined): string | undefined {
  if (!entry) return undefined;
  if (
    entry.unreadCompletedAt !== undefined &&
    (entry.completedAt === undefined ||
      (Date.parse(entry.unreadCompletedAt) >= Date.parse(entry.completedAt) &&
        entry.unreadAt! >= entry.observedAt!))
  )
    return undefined;
  return entry.completedAt;
}
